import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from './api';
import './App.css';

// Updated to match the new backend response structure
interface OrganizationItem {
  id: string;
  name: string;
  summary: string;
  description?: string | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
  hidden?: boolean;
  isTopLevel?: boolean;
}

const OrganizationPage: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<OrganizationItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const pageSize = 10; // Or make this configurable

  useEffect(() => {
    const fetchOrganizations = async (page: number) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('include', 'id');
        params.append('include', 'name');
        params.append('include', 'summary');
        params.append('include', 'description');
        params.append('include', 'imageUrl');
        params.append('include', 'imageAlt');
        params.append('include', 'hidden');
        params.append('include', 'isTopLevel');

        const response = await api(`/organizations?${params.toString()}&page=${page}&pageSize=${pageSize}`);
        setOrganizations(response.organizations);
        setTotalPages(response.totalPages);
        setCurrentPage(response.currentPage);
      } catch (error) {
        setError('Failed to fetch organizations.');
        console.error('failed to fetch organizations', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizations(currentPage);
  }, [currentPage]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">{t('organization_page_title', 'Our Partner Organizations')}</h1>
      
      {user && (
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <Link to="/organization/new" className="button-primary">Create Organization</Link>
        </div>
      )}

      {isLoading && <p style={{ textAlign: 'center' }}>Loading organizations...</p>}
      {error && <p className="form-error" style={{ maxWidth: '600px', margin: '0 auto' }}>{error}</p>}

      <div className="event-list grid-view">
        {!isLoading && !error && organizations.length === 0 ? (
          <p style={{ textAlign: 'center' }}>{t('no_organizations_available', 'No organizations to display at the moment.')}</p>
        ) : (
          organizations.map(org => (
            <div key={org.id} className="event-card-grid-item organization-card-grid-item">
              {org.imageUrl ? (
                <img 
                  src={org.imageUrl} 
                  alt={org.imageAlt || `${org.name} logo`} 
                  className="event-image" 
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="event-image placeholder-image">
                  <div className="placeholder-content">
                    <span>{org.name.charAt(0).toUpperCase()}</span>
                  </div>
                </div>
              )}
              <div className="event-details">
                <h3 className="event-name">{org.name}</h3>
                <p className="organization-summary">{org.summary}</p>
                {org.description && (
                  <p className="organization-description">{org.description}</p>
                )}
                {user && (user.role === 'admin' || user.role === 'org_admin') && (
                  <Link to={`/organization/${org.id}/users`} className="button-secondary">Manage Users</Link>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="pagination-controls">
        <button onClick={handlePreviousPage} disabled={currentPage === 1 || isLoading} className="pagination-button">
          {t('pagination_previous', 'Previous')}
        </button>
        <span className="pagination-info">
          {t('pagination_page_info', { currentPage, totalPages })}
        </span>
        <button onClick={handleNextPage} disabled={currentPage === totalPages || isLoading} className="pagination-button">
          {t('pagination_next', 'Next')}
        </button>
      </div>
    </div>
  );
};

export default OrganizationPage; 
